import { forwardRef, useMemo } from 'react';
import { PropertiesProvider as CustomerDataPropertiesProvider } from 'customer-data-properties/context';
import AsyncListingLibDevTools from 'framework-listing-lib/internal/DevTools/components/AsyncListingLibDevTools';
import useOpenDevTools from 'framework-listing-lib/internal/DevTools/hooks/useOpenDevTools';
import ErrorBoundary from 'framework-listing-lib/internal/ErrorBoundary';
import ViewsProvider from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import usePerformanceTracking from 'framework-listing-lib/internal/hooks/usePerformanceTracking';
import CrmObjectTypeListingClientProvider from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import DataProvider from 'framework-listing-lib/internal/providers/DataProvider';
import ListingProvider from 'framework-listing-lib/internal/providers/ListingProvider';
import PropertiesProvider from 'framework-listing-lib/internal/providers/PropertiesProvider';
import TrackerProvider from 'framework-listing-lib/internal/providers/TrackerProvider';
import { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import { SENTRY_KEYS } from 'framework-listing-lib/utils/sentry';
import UserInfoProvider from 'marketing-platform-lib/providers/UserInfoProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function FrameworkListingLibProvider({
  associationSpecs,
  calendarProps,
  children,
  emptyStateFilter,
  enableFieldLevelPermissions = false,
  enableObjectLevelPermissions = false,
  fetcher,
  folderProps,
  hiddenFilter,
  initialFilter,
  inlineEditProps,
  objectType,
  propertyOverride,
  typeDefinitionOverride,
  quickFilterProps,
  searchInputProps: _searchInputProps,
  trackerProps,
  uiModeProps,
  viewProps,
  showHighlySensitiveProperties,
  skipCrm,
  sortPropertyOverride,
  _useNonGqlCRMSearch
}) {
  const [open] = useOpenDevTools();
  usePerformanceTracking(MEASURE_MARK_NAMES.LIFECYCLE);
  const searchInputProps = useMemo(() => {
    if (_searchInputProps) {
      return Object.assign({}, _searchInputProps, {
        // Enable search input by default, unless explicitly hidden by app teams.
        hasSearchInput: typeof _searchInputProps.hasSearchInput === 'boolean' ? _searchInputProps.hasSearchInput : true
      });
    }
    return {
      hasSearchInput: true
    };
  }, [_searchInputProps]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fingerprint: SENTRY_KEYS.TOP_LEVEL_LIB_PROVIDER,
    children: /*#__PURE__*/_jsx(UserInfoProvider, {
      children: /*#__PURE__*/_jsx(TrackerProvider, Object.assign({}, trackerProps, {
        children: /*#__PURE__*/_jsx(CustomerDataPropertiesProvider, {
          enableFieldLevelPermissions: enableFieldLevelPermissions,
          children: /*#__PURE__*/_jsx(ListingProvider, {
            enableObjectLevelPermissions: enableObjectLevelPermissions,
            objectType: objectType,
            uiModeProps: uiModeProps,
            viewProps: viewProps,
            skipCrm: skipCrm,
            _useNonGqlCRMSearch: _useNonGqlCRMSearch,
            children: /*#__PURE__*/_jsx(PropertiesProvider, {
              propertyOverride: propertyOverride,
              sortPropertyOverride: sortPropertyOverride,
              typeDefinitionOverride: typeDefinitionOverride,
              children: /*#__PURE__*/_jsx(DataProvider, {
                showHighlySensitiveProperties: showHighlySensitiveProperties,
                children: /*#__PURE__*/_jsx(ViewsProvider, {
                  onChangeUIMode: uiModeProps ? uiModeProps.onChangeUIMode : undefined,
                  children: /*#__PURE__*/_jsxs(CrmObjectTypeListingClientProvider, {
                    associationSpecs: associationSpecs,
                    calendarProps: calendarProps,
                    emptyStateFilter: emptyStateFilter,
                    fetcher: fetcher,
                    folderProps: folderProps,
                    hiddenFilter: hiddenFilter,
                    initialFilter: initialFilter,
                    inlineEditProps: inlineEditProps,
                    quickFilterProps: quickFilterProps,
                    searchInputProps: searchInputProps,
                    children: [open && /*#__PURE__*/_jsx(AsyncListingLibDevTools, {}), children]
                  })
                })
              })
            })
          })
        })
      }))
    })
  });
}
export default $hasDep('react-dnd', '5') ? FrameworkListingLibProvider :
// eslint-disable-next-line @typescript-eslint/no-var-requires
require('customer-data-ui-utilities/dnd/SharedDragDropContext').default()( /*#__PURE__*/forwardRef((props, __ref) => /*#__PURE__*/_jsx(FrameworkListingLibProvider, Object.assign({}, props))));